<template>
  <div>
    <b-card>
      <b-card-header>
        <b-card-title class="mb-0">E-Learning [CPD] Course Attendance</b-card-title>
      </b-card-header>

      <b-card-body>
        <!-- One row from xl up; wraps on smaller screens. -->
        <div class="filter-panel mb-2">
          <b-row class="filter-row">
            <b-col cols="12" sm="6" md="4" xl class="filter-col filter-col--date">
              <label class="filter-label">Date range</label>
              <flat-pickr
                v-model="filters.range"
                class="form-control"
                :config="{ mode: 'range', dateFormat: 'Y-m-d' }"
                placeholder="Whole year"
              />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Companies" :options="companies" v-model="filters.company" select-all @input="onCompanyChange" />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Sites" :options="filteredSites" v-model="filters.site" select-all />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Roles" :options="roles" v-model="filters.role" select-all />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Courses" :options="courses" v-model="filters.course" select-all />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl="auto" class="filter-col d-flex align-items-end">
              <b-button variant="outline-secondary" class="filter-reset" @click="resetFilters">
                <feather-icon icon="RotateCcwIcon" size="14" class="mr-25" />Reset
              </b-button>
            </b-col>
          </b-row>
        </div>

        <section class="report-panel">
          <div class="block-heading">
            <span class="block-heading__icon">
              <feather-icon icon="MonitorIcon" size="16" />
            </span>
            <h5 class="mb-0">E-Learning [CPD] Summary</h5>
          </div>

          <!-- Six tiles: one row only from xl up, where each is wide enough
               for "CPD credits awarded" without truncating. -->
          <b-row class="kpi-row">
            <b-col cols="12" sm="6" lg="4" xl="2" class="mb-1" v-for="tile in tiles" :key="tile.label">
              <kpi-tile v-bind="tile" />
            </b-col>
          </b-row>
        </section>

        <section class="report-panel">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">
              {{ summary.completions }} completion{{ summary.completions === 1 ? "" : "s" }}
              <small class="text-muted ml-50">{{ rangeLabel }}</small>
            </h5>
            <b-button
              size="sm"
              variant="success"
              :disabled="loading || !summary.completions"
              @click="doExport"
            >
              Export
            </b-button>
          </div>

          <div v-if="loading" class="text-center py-3">
            <b-spinner small class="mr-1" /> Loading...
          </div>

          <div v-else-if="!summary.completions" class="text-center text-muted py-3">
            No E-Learning [CPD] completions match these filters.
          </div>

          <b-tabs v-else pills>
            <b-tab title="By participant" active>
              <div class="table-responsive mt-1" style="max-height: 520px; overflow: auto;">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Participant</th>
                      <th>Division</th>
                      <th>Role</th>
                      <th class="text-center">Courses completed</th>
                      <th class="text-center">CPD credits</th>
                      <th class="text-center">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="row in byLearner">
                      <tr :key="row.user_id" class="expandable-row" @click="toggle(expandedLearners, row.user_id)">
                        <td>
                          <span class="chevron">{{ expandedLearners[row.user_id] ? "&#9662;" : "&#9656;" }}</span>
                          {{ row.learner }}
                        </td>
                        <td>{{ row.division || "-" }}</td>
                        <td>{{ row.role || "-" }}</td>
                        <td class="text-center">{{ row.completions }}</td>
                        <td class="text-center">{{ row.cpd_credits }}</td>
                        <td class="text-center">{{ row.hours }}</td>
                      </tr>
                      <tr v-if="expandedLearners[row.user_id]" :key="`l-${row.user_id}-detail`">
                        <td colspan="6" class="p-0">
                          <table class="table table-sm detail-table mb-0">
                            <thead>
                              <tr>
                                <th>Course completed</th>
                                <th class="text-center">Date completed</th>
                                <th class="text-center">Grade</th>
                                <th class="text-center">CPD credits</th>
                                <th class="text-center">Hours</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(c, i) in row.course_rows" :key="`${row.user_id}-${c.course_id}-${i}`">
                                <td>{{ c.course }}</td>
                                <td class="text-center">{{ c.completed_at }}</td>
                                <td class="text-center">{{ c.grade === null ? "-" : c.grade + "%" }}</td>
                                <td class="text-center">{{ c.cpd_credits === null ? "-" : c.cpd_credits }}</td>
                                <td class="text-center">{{ c.hours === null ? "-" : c.hours }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </b-tab>

            <b-tab title="By course">
              <div class="table-responsive mt-1" style="max-height: 520px; overflow: auto;">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th class="text-center">Completions</th>
                      <th class="text-center">Participants</th>
                      <th class="text-center">CPD credits</th>
                      <th class="text-center">Hours</th>
                      <th class="text-center">Avg grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="row in byCourse">
                      <tr :key="row.course_id" class="expandable-row" @click="toggle(expandedCourses, row.course_id)">
                        <td>
                          <span class="chevron">{{ expandedCourses[row.course_id] ? "&#9662;" : "&#9656;" }}</span>
                          {{ row.course }}
                        </td>
                        <td class="text-center">{{ row.completions }}</td>
                        <td class="text-center">{{ row.learners }}</td>
                        <td class="text-center">{{ row.cpd_credits }}</td>
                        <td class="text-center">{{ row.hours }}</td>
                        <td class="text-center">{{ row.avg_grade === null ? "-" : row.avg_grade + "%" }}</td>
                      </tr>
                      <tr v-if="expandedCourses[row.course_id]" :key="`c-${row.course_id}-detail`">
                        <td colspan="6" class="p-0">
                          <table class="table table-sm detail-table mb-0">
                            <thead>
                              <tr>
                                <th>Staff</th>
                                <th>Division</th>
                                <th>Role</th>
                                <th class="text-center">Date completed</th>
                                <th class="text-center">Grade</th>
                                <th class="text-center">CPD credits</th>
                                <th class="text-center">Hours</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(l, i) in row.learner_rows" :key="`${row.course_id}-${l.user_id}-${i}`">
                                <td>{{ l.learner }}</td>
                                <td>{{ l.division || "-" }}</td>
                                <td>{{ l.role || "-" }}</td>
                                <td class="text-center">{{ l.completed_at }}</td>
                                <td class="text-center">{{ l.grade === null ? "-" : l.grade + "%" }}</td>
                                <td class="text-center">{{ l.cpd_credits === null ? "-" : l.cpd_credits }}</td>
                                <td class="text-center">{{ l.hours === null ? "-" : l.hours }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </b-tab>

            <b-tab title="Over time">
              <div class="table-responsive mt-1" style="max-height: 520px; overflow: auto;">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th class="text-center">Completions</th>
                      <th class="text-center">CPD credits</th>
                      <th class="text-center">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in byMonth" :key="row.month">
                      <td>{{ row.month }}</td>
                      <td class="text-center">{{ row.completions }}</td>
                      <td class="text-center">{{ row.cpd_credits }}</td>
                      <td class="text-center">{{ row.hours }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-tab>
          </b-tabs>
        </section>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BTabs,
  BTab,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import store from "@/store";
import reportStoreModule from "./reportStoreModule";
import KpiTile from "./KpiTile.vue";
import FilterSelect from "./FilterSelect.vue";

const STORE_MODULE = "trainingReports";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BTabs,
    BTab,
    flatPickr,
    KpiTile,
    FilterSelect,
  },
  data() {
    return {
      loading: false,
      loadTimer: null,
      companies: [],
      sites: [],
      roles: [],
      courses: [],
      filters: { range: null, company: [], site: [], role: [], course: [] },
      summary: { completions: 0, learners: 0, courses: 0, events: 0, cpd_credits: 0, hours: 0 },
      range: { start: null, end: null },
      byLearner: [],
      byCourse: [],
      byMonth: [],
      // Which drill-down rows are open, keyed by user_id / course_id.
      expandedLearners: {},
      expandedCourses: {},
    };
  },
  computed: {
    filteredSites() {
      const ids = (this.filters.company || []).map((c) => c.id);
      if (!ids.length) return this.sites;
      return this.sites.filter((s) => ids.includes(s.company_id));
    },
    tiles() {
      return [
        { label: "Courses", value: this.summary.courses, icon: "BookOpenIcon" },
        { label: "Events", value: this.summary.events, icon: "CalendarIcon" },
        { label: "Participants", value: this.summary.learners, icon: "UserCheckIcon" },
        { label: "Completions", value: this.summary.completions, icon: "CheckCircleIcon" },
        { label: "CPD credits awarded", value: this.summary.cpd_credits, icon: "StarIcon" },
        { label: "Hours to complete", value: this.summary.hours, icon: "ClockIcon" },
      ];
    },
    rangeLabel() {
      if (!this.range.start) return "";
      return `${this.range.start} to ${this.range.end}`;
    },
  },
  watch: {
    filters: {
      handler() {
        this.queueLoad();
      },
      deep: true,
    },
  },
  created() {
    // Report.vue owns the shared module's lifetime; this only covers being
    // mounted on its own.
    if (!store.hasModule(STORE_MODULE)) store.registerModule(STORE_MODULE, reportStoreModule);
    this.loadOptions().then(() => this.loadData());
  },
  beforeDestroy() {
    clearTimeout(this.loadTimer);
  },
  methods: {
    queueLoad() {
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => this.loadData(), 250);
    },
    loadOptions() {
      return store
        .dispatch("trainingReports/getCpdOptions")
        .then((res) => {
          this.companies = res.data.companies || [];
          this.sites = res.data.sites || [];
          this.roles = res.data.roles || [];
          this.courses = res.data.courses || [];
        })
        .catch((err) => {
          this.$swal({ title: "Error", text: "Failed to load filters: " + err, icon: "error" });
        });
    },
    // Drop site selections that no longer belong to the chosen companies.
    // Only reassign on an actual change, or the deep watcher loops.
    onCompanyChange() {
      const siteIds = this.filteredSites.map((s) => s.id);
      const site = (this.filters.site || []).filter((s) => siteIds.includes(s.id));
      if (site.length !== (this.filters.site || []).length) this.filters.site = site;
    },
    buildParams() {
      const params = {
        company_id: (this.filters.company || []).map((c) => c.id).join(",") || undefined,
        site_id: (this.filters.site || []).map((s) => s.id).join(",") || undefined,
        role: (this.filters.role || []).map((r) => r.id).join(",") || undefined,
        course_id: (this.filters.course || []).map((c) => c.id).join(",") || undefined,
      };

      // flat-pickr range mode yields "start to end"; a half-picked range has
      // only one date, in which case leave the server on its default year.
      const parts = (this.filters.range || "").split(" to ");
      if (parts.length === 2) {
        params.start_date = parts[0];
        params.end_date = parts[1];
      }

      return params;
    },
    loadData() {
      this.loading = true;
      store
        .dispatch("trainingReports/getCpdAttendance", this.buildParams())
        .then((res) => {
          // Drop open drill-downs - the ids they point at may be gone.
          this.expandedLearners = {};
          this.expandedCourses = {};
          this.summary = res.data.summary;
          this.range = res.data.range;
          this.byLearner = res.data.by_learner || [];
          this.byCourse = res.data.by_course || [];
          this.byMonth = res.data.by_month || [];
        })
        .catch((err) => {
          this.$swal({ title: "Error", text: "Failed to load E-Learning [CPD] report: " + err, icon: "error" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetFilters() {
      this.filters = { range: null, company: [], site: [], role: [], course: [] };
    },
    // $set, because Vue 2 cannot see keys added to a plain object.
    toggle(map, id) {
      this.$set(map, id, !map[id]);
    },
    doExport() {
      store
        .dispatch("trainingReports/exportCpdAttendance", this.buildParams())
        .then((res) => this.saveBlobResponse(res))
        .catch((err) => {
          this.$swal({ title: "Error", text: "Failed to export: " + err, icon: "error" });
        });
    },
    saveBlobResponse(res) {
      const disposition = res.headers && res.headers["content-disposition"];
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : "cpd_attendance.xlsx";
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.table { font-size: 0.85rem; }
.filter-panel {
  background: #f8f8f8;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem 0.25rem;
}
.filter-row { margin-left: -0.4rem; margin-right: -0.4rem; }
.filter-col { padding-left: 0.4rem; padding-right: 0.4rem; margin-bottom: 0.5rem; min-width: 0; }
.filter-reset { height: 38px; white-space: nowrap; }
@media (min-width: 1200px) {
  /* Wide enough for "2026-01-01 to 2026-12-31". */
  .filter-col--date { flex: 0 0 14rem; max-width: 14rem; }
}
.dark-layout .filter-panel { background: #283046; border-color: #3b4253; }
.filter-label { font-size: 0.75rem; font-weight: 600; text-transform: uppercase; color: #6e6b7b; margin-bottom: 0.25rem; display: block; }
/* Same panel look as the Overview's E-Learning [CPD] block, teal accent. */
.report-panel {
  --accent: #00a8c2;
  --accent-soft: rgba(0, 207, 232, 0.12);
  border: 1px solid #ebe9f1;
  border-left: 4px solid var(--accent);
  border-radius: 0.5rem;
  padding: 1rem 1.25rem 1.25rem;
  margin-bottom: 1.5rem;
  background: #fff;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.05);
}
.block-heading {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
}
.block-heading h5 { font-weight: 600; color: #5e5873; }
.block-heading__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent);
}
.kpi-row { margin-bottom: -0.25rem; }
.dark-layout .report-panel { background: #283046; border-color: #3b4253; border-left-color: var(--accent); }
.dark-layout .block-heading h5 { color: #d0d2d6; }
.expandable-row { cursor: pointer; }
.expandable-row:hover { background: #f3f2f7; }
.chevron { display: inline-block; width: 0.9rem; color: #6e6b7b; }
.detail-table { background: #fafafa; font-size: 0.8rem; }
.detail-table th { font-weight: 600; color: #6e6b7b; }
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
